import React, {useEffect, useState} from 'react';
import {Autocomplete, Checkbox, Chip, CircularProgress, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import {
    filterExcludeColor,
    filterIncludeColor,
    getStringValueForStatus,
    STATUS_GROUPS,
    STATUSES_TO_STRING_VALUE,
} from '../../../utils/constants';
import {useDebounce} from '../../_helpers';
import {BrickContext} from 'olympe';
import {checkLabelValueObject} from '../../../utils/checkObjects';
import {getFiltersDropdownsOptionsLambda} from '../../../utils/functionSignatures';


/**
 * @param {FilterAutocomplete.propTypes} props
 * @return {Element}
 * @constructor
 */
export const FilterAutocomplete = (props) => {
    const {getOptionLabel} = props;
    const dataCy = `filter-${props.field || props.id || 'field'}`;

    const [textInput, setTextInput] = useState('');
    const debouncedTextInput = useDebounce(textInput, 800);
    const [queriedText, setQueriedText] = useState('');
    // A query is performed
    // - when the user opens the autocomplete
    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [groupStatusState, setGroupStatusState] = useState({});

    // This useEffect handles the queries to perform to get the options for the autocomplete
    useEffect(() => {
        // avoid performing several queries simultaneously
        if (isPerformingQuery) {
            return;
        }
        const searchInput = debouncedTextInput.trim();
        // avoid performing a query of with the same input as the last query
        if (queriedText === searchInput && options.length > 0) {
            return;
        }
        // avoid performing a query when the autocomplete is closed
        if (open === false) {
            return;
        }
        // start a query
        setIsPerformingQuery(true);
        if (props.myRequestOnly !== null && props.myRequestOnly === false && props.filteredOptions && props.filteredOptions?.[props.field]?.length > 0) {
            setOptions(props.filteredOptions[props.field]);
            setQueriedText(searchInput);
            setIsPerformingQuery(false);
            return;
        }

        getFiltersDropdownsOptionsLambda(props.context, props.getFiltersDropdownsOptions, props.currentTab, props.myRequestOnly, props.userId, props.field, searchInput)
            .then((result) => {
                const filteredResult = JSON.parse(result)
                    .map((v) => {
                        if ('v0' in v && 'v1' in v && v.v0) {
                            const value = v.v0;
                            let label = v.v1;
                            if (props.field === 'vendor') {
                                label = `${value} - ${label}`;
                            }
                            return checkLabelValueObject({value: value, label: label});
                        } else if ('v0' in v && v.v0) {
                            let label = v.v0;
                            if (props.field === 'id') {
                                label = `EPR-${label}`;
                            } else if (props.field === 'status') {
                                label = STATUSES_TO_STRING_VALUE[label];
                            }
                            return checkLabelValueObject({value: v.v0, label: label});
                        } else {
                            return ({label: 'Blank', value: ''});
                        }
                    });

                // We keep only one Blank option if needed, and put it at the end, except for supplier status, as there's
                // a specific status called "Blank"
                let finalResults = filteredResult.filter((item) => !(item.label === 'Blank' && item.value === ''));
                if (filteredResult.some((item) => item.label === 'Blank' && item.value === '') &&
                        props.field !== 'supplierStatus') {
                    finalResults = finalResults.concat([{label: 'Blank', value: ''}]);
                }

                setOptions(finalResults);
                setQueriedText(searchInput);
                setIsPerformingQuery(false);
            })
            .catch(() => {
                setOptions([]);
                setQueriedText(searchInput);
                setIsPerformingQuery(false);
            });
    }, [open, debouncedTextInput, queriedText]);

    useEffect(() => {
        updateGroupSelectionState(props.selectedObject.selected);
    }, [props.selectedObject.selected]);

    const updateGroupSelectionState = (selectedOptions) => {
        const newGroupStatusState = {};

        Object.keys(STATUS_GROUPS).forEach((group) => {
            const groupStatuses = STATUS_GROUPS[group].statuses.map((status) => getStringValueForStatus(status));
            const selectedGroupOptions = selectedOptions.filter((option) => groupStatuses.includes(option.label));

            if (selectedGroupOptions.length === groupStatuses.length) {
                newGroupStatusState[group] = 'checked';
            } else if (selectedGroupOptions.length > 0) {
                newGroupStatusState[group] = 'indeterminate';
            } else {
                newGroupStatusState[group] = 'unchecked';
            }
        });

        setGroupStatusState(newGroupStatusState);
    };

    const handleChange = (event, selectedOptions, reason) => {
        let expandedSelection = [...selectedOptions];
        const localGroupStatusState = {...groupStatusState};

        if (reason === 'selectOption' || reason === 'removeOption') {
            selectedOptions.forEach((option) => {
                const group = Object.keys(STATUS_GROUPS).find((group) => STATUS_GROUPS[group].label === option.label);

                if (group) {
                    const groupStatuses = STATUS_GROUPS[group].statuses.map((status) => ({
                        label: getStringValueForStatus(status),
                        value: status,
                    }));

                    const isGroupChecked = localGroupStatusState[group] === 'checked';

                    if (isGroupChecked) {
                        expandedSelection = expandedSelection.filter(
                            (opt) => !groupStatuses.some((status) => status.label === opt.label),
                        );
                        localGroupStatusState[group] = 'unchecked';
                    } else {
                        groupStatuses.forEach((status) => {
                            if (!expandedSelection.some((opt) => opt.label === status.label)) {
                                expandedSelection.push(status);
                            }
                        });
                        localGroupStatusState[group] = 'checked';
                    }
                }
            });

            expandedSelection = expandedSelection.filter((option) => {
                const group = Object.keys(STATUS_GROUPS).find((group) => STATUS_GROUPS[group].label === option.label);
                return !group;
            });

            setGroupStatusState(localGroupStatusState);
            props.handleChange && props.handleChange(expandedSelection);
        } else if (reason === 'clear') {
            props.onClearOptions && props.onClearOptions();
        }

        updateGroupSelectionState(expandedSelection);
    };

    const handleInputChange = (event) => {
        const inputValue = event.target.value;
        setTextInput(inputValue);
    };

    const renderOption = (props, option, {selected}) => {
        const group = Object.keys(STATUS_GROUPS).find((group) => STATUS_GROUPS[group].label === option.label);
        const isGroup = !!group;

        const checkboxProps = {
            checked: selected,
            indeterminate: false,
        };

        if (isGroup) {
            checkboxProps.checked = groupStatusState[group] === 'checked';
            checkboxProps.indeterminate = groupStatusState[group] === 'indeterminate';
        }

        return (
            <li
                {...props}
                style={{
                    // If the label is Blank, show it different from other options.
                    fontStyle: option.label === 'Blank' && option.value === '' ? 'italic' : 'unset',
                    color: option.label === 'Blank' && option.value === '' ? '#AAA' : '#000',
                }}
            >
                <Checkbox {...checkboxProps} />
                {getOptionLabel ? getOptionLabel(option) : option.label}
            </li>
        );
    };

    return (
        <Autocomplete
            multiple
            id={props.id}
            data-cy={dataCy}
            PopperComponent={(val) => props.popperComponent(val)}
            limitTags={props.limitTags}
            options={options}
            getOptionLabel={(option) => getOptionLabel ? getOptionLabel(option) : option.label.toString()}
            isOptionEqualToValue={(option, value) => option.label === value.label}
            filterOptions={(x) => x}
            disableCloseOnSelect
            fullWidth
            loading={isPerformingQuery}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            size={'small'}
            value={props.selectedObject.selected}
            onChange={handleChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    data-cy={`${dataCy}-input`}
                    label={props.label}
                    onChange={handleInputChange}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            props.loading || isPerformingQuery ?
                                <React.Fragment>
                                    <CircularProgress size={20} />
                                </React.Fragment> : params.InputProps.startAdornment
                        ),
                    }}/>
            )}
            renderOption={renderOption}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        {...getTagProps({index})}
                        data-cy={`${dataCy}-chip`}
                        variant={'filled'}
                        key={option.label}
                        label={getOptionLabel ? getOptionLabel(option) : option.label}
                        size={'small'}
                        sx={{
                            backgroundColor: props.selectedObject.isInclude ? filterIncludeColor : filterExcludeColor,
                        }}
                    />
                ))}
        />
    );
};

FilterAutocomplete.propTypes = {
    selectedObject: PropTypes.object,
    id: PropTypes.string,
    label: PropTypes.string,
    handleChange: PropTypes.func,
    onClearOptions: PropTypes.func,
    popperComponent: PropTypes.func,
    getOptionLabel: PropTypes.func,
    limitTags: PropTypes.number,
    field: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    myRequestOnly: PropTypes.bool,
    userId: PropTypes.string,
    loading: PropTypes.bool,
    filteredOptions: PropTypes.object,
    currentTab: PropTypes.string,
    getFiltersDropdownsOptions: PropTypes.object,
};
